/**
 * Universal Texture Mapping System — Coordinator
 * ----------------------------------------------
 * Resolves a scene object's `texture_mapping_mode` (auto | uv | triplanar)
 * into a plugin-enable decision on each of its rendered meshes.
 *
 *   auto  → UV if the mesh has valid UV data, else Triplanar
 *   uv    → force UV sampling (disable plugin)
 *   triplanar → force Triplanar (enable plugin)
 *
 * The UV validity check runs ONCE per mesh at first attach and is cached on
 * `mesh.metadata.uvValid` (never recomputed per frame).
 *
 * Does not touch geometry, does not duplicate materials, does not call into
 * the projection system — purely a UV-override on the diffuse sampler.
 */
import * as BABYLON from '@babylonjs/core';
import { ensureTriplanarPlugin, TRIPLANAR_PLUGIN_NAME } from './TriplanarMappingPlugin';

// UV is considered "valid" when:
//   • the mesh has a UVKind buffer,
//   • UV coverage (maxU-minU, maxV-minV) is not collapsed to a point, AND
//   • the UV buffer is non-degenerate (length > 0).
// These are the three failure modes that actually produce visibly wrong
// textures — mildly stretched UVs still look fine in practice.
const MIN_UV_RANGE = 0.01;

export function checkUVValidity(mesh) {
  if (!mesh || typeof mesh.isVerticesDataPresent !== 'function') return false;
  if (!mesh.isVerticesDataPresent(BABYLON.VertexBuffer.UVKind)) return false;
  const uvs = mesh.getVerticesData(BABYLON.VertexBuffer.UVKind);
  if (!uvs || uvs.length < 4) return false;

  let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
  // Sample at most 2000 verts to keep this O(1) for huge meshes.
  const stride = Math.max(2, Math.floor(uvs.length / 4000) * 2);
  for (let i = 0; i < uvs.length; i += stride) {
    const u = uvs[i], v = uvs[i + 1];
    if (u < minU) minU = u; if (u > maxU) maxU = u;
    if (v < minV) minV = v; if (v > maxV) maxV = v;
  }
  const uRange = maxU - minU;
  const vRange = maxV - minV;
  if (uRange < MIN_UV_RANGE && vRange < MIN_UV_RANGE) return false;
  // NaN guard
  if (!Number.isFinite(uRange) || !Number.isFinite(vRange)) return false;
  return true;
}

function collectRenderMeshes(root) {
  const out = [];
  if (!root) return out;
  const visit = (n) => {
    if (n && n.material) out.push(n);
    if (n && typeof n.getChildMeshes === 'function') {
      n.getChildMeshes(false).forEach((c) => {
        if (c.material) out.push(c);
      });
    }
  };
  visit(root);
  return out;
}

/**
 * Apply the requested texture-mapping mode to a mesh (or transform node with
 * child meshes).  Safe to call repeatedly — idempotent and cheap.
 *
 * @param {BABYLON.AbstractMesh | BABYLON.TransformNode} root
 * @param {'auto' | 'uv' | 'triplanar'} mode
 * @param {{ scale?: number }} [opts]
 */
export function applyTextureMapping(root, mode = 'auto', opts = {}) {
  const scale = Number(opts.scale) > 0 ? opts.scale : 1.0;
  const meshes = collectRenderMeshes(root);

  for (const m of meshes) {
    // Only StandardMaterial is supported in v1 — PBRMaterial uses different
    // sampler names and would need a sibling plugin. Loaded GLBs almost
    // always render as PBR in Babylon; OBJ/MTL + primitives render as Standard.
    // When PBR is encountered we silently skip (mesh keeps UV sampling).
    const mat = m.material;
    if (!(mat instanceof BABYLON.StandardMaterial)) continue;

    // Run UV-validity check once and cache on metadata.
    if (m.metadata?.uvValid === undefined) {
      const valid = checkUVValidity(m);
      m.metadata = { ...(m.metadata || {}), uvValid: valid };
    }

    // Resolve effective mode.
    let effective = mode;
    if (effective === 'auto') {
      effective = m.metadata.uvValid ? 'uv' : 'triplanar';
    }

    const plugin = ensureTriplanarPlugin(mat);
    // ensureTriplanarPlugin may return null if the engine refused to accept
    // the plugin (e.g. WGSL-only build). In that case the mesh stays in
    // standard UV sampling — a graceful degradation, not a crash.
    if (!plugin) continue;
    try {
      plugin.isEnabled = effective === 'triplanar';
      plugin.scale = scale;
    } catch (e) {
      // Any engine-side error toggling the plugin should never bubble
      // into React — log once and keep going.
      // eslint-disable-next-line no-console
      console.warn('[textureMappingCoordinator] plugin toggle failed:', e && e.message);
      continue;
    }

    // Texture wrap mode matters for triplanar (world-space coords often go
    // outside 0..1). Force WRAP_ADDRESSMODE so the sample doesn't clamp.
    if (plugin.isEnabled && mat.diffuseTexture) {
      mat.diffuseTexture.wrapU = BABYLON.Texture.WRAP_ADDRESSMODE;
      mat.diffuseTexture.wrapV = BABYLON.Texture.WRAP_ADDRESSMODE;
    }
  }
}

/**
 * Convenience: disable triplanar everywhere on a mesh (used when texture is
 * removed — resets the material to its default UV-sampling path).
 */
export function disableTextureMapping(root) {
  const meshes = collectRenderMeshes(root);
  for (const m of meshes) {
    const mat = m.material;
    if (!(mat instanceof BABYLON.StandardMaterial)) continue;
    const plugin = mat.pluginManager && mat.pluginManager.getPlugin
      ? mat.pluginManager.getPlugin(TRIPLANAR_PLUGIN_NAME)
      : null;
    if (plugin) plugin.isEnabled = false;
  }
}
